import { resolveDataVizLayout } from "../layout.js";
import { dataVizColorToNumber, resolveDataVizTheme } from "../theme.js";
const DEFAULT_LABEL_NODE_KINDS = new Set([
    "route",
    "report",
    "datasource",
    "function",
    "operation",
    "schema",
    "entity",
    "field",
    "join",
    "pivot",
    "protocol",
    "artifact",
]);
export const serializeDataVizVector = (value) => [value.x, value.y, value.z].map((entry) => entry.toFixed(2)).join(",");
const formatDataVizCanvasLabel = (label) => label
    .replace(/^Function:/, "Fn:")
    .replace(/^Datasource:/, "Source:")
    .replace(/^Operation:/, "Op:");
const truncateDataVizLabel = (label, maxLength = 24) => {
    const trimmed = label.trim();
    if (trimmed.length <= maxLength) {
        return trimmed;
    }
    return `${trimmed.slice(0, Math.max(maxLength - 3, 0)).trimEnd()}...`;
};
const drawLabelBackground = (context, width, height) => {
    const radius = 8;
    const x = 1;
    const y = 1;
    const w = width - 2;
    const h = height - 2;
    context.beginPath();
    context.moveTo(x + radius, y);
    context.lineTo(x + w - radius, y);
    context.quadraticCurveTo(x + w, y, x + w, y + radius);
    context.lineTo(x + w, y + h - radius);
    context.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    context.lineTo(x + radius, y + h);
    context.quadraticCurveTo(x, y + h, x, y + h - radius);
    context.lineTo(x, y + radius);
    context.quadraticCurveTo(x, y, x + radius, y);
    context.closePath();
    context.fill();
    context.stroke();
};
export const createDataVizLabelSprite = (THREE, label, theme) => {
    if (typeof document === "undefined") {
        return null;
    }
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
        return null;
    }
    const text = truncateDataVizLabel(formatDataVizCanvasLabel(label));
    const pixelRatio = 2;
    const fontSize = 14;
    const paddingX = 10;
    const paddingY = 6;
    context.font = `700 ${fontSize}px Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    const metrics = context.measureText(text);
    const width = Math.ceil(metrics.width + paddingX * 2);
    const height = fontSize + paddingY * 2;
    canvas.width = width * pixelRatio;
    canvas.height = height * pixelRatio;
    context.scale(pixelRatio, pixelRatio);
    context.font = `700 ${fontSize}px Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    context.textBaseline = "middle";
    context.fillStyle = theme.labelBackgroundColor;
    context.strokeStyle = theme.labelBorderColor;
    context.lineWidth = 1;
    drawLabelBackground(context, width, height);
    context.fillStyle = theme.labelTextColor;
    context.fillText(text, paddingX, height / 2);
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
        depthTest: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(width * 0.18, height * 0.18, 1);
    return sprite;
};
const clampDataVizOverlayPosition = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));
const createDataVizChartOverlayLabelElement = (anchor, theme) => {
    const element = document.createElement("div");
    element.setAttribute("data-testid", "data-viz-three-chart-overlay-label");
    element.setAttribute("data-overlay-kind", anchor.kind);
    element.setAttribute("data-overlay-anchor-id", anchor.id);
    element.setAttribute("aria-label", `${anchor.label}: ${anchor.value}`);
    element.style.position = "absolute";
    element.style.boxSizing = "border-box";
    element.style.maxWidth = anchor.kind === "legend" ? "280px" : "230px";
    element.style.minWidth = anchor.kind === "legend" ? "180px" : "136px";
    element.style.minHeight = "32px";
    element.style.padding = anchor.kind === "legend" ? "8px 10px" : "7px 10px";
    element.style.border = `1px solid ${theme.labelBorderColor}`;
    element.style.borderRadius = "8px";
    element.style.background = theme.labelBackgroundColor;
    element.style.color = theme.labelTextColor;
    element.style.boxShadow = "0 10px 28px rgba(15, 23, 42, 0.16)";
    element.style.font = "700 13px/1.25 Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif";
    element.style.letterSpacing = "0";
    element.style.overflowWrap = "anywhere";
    element.style.pointerEvents = "none";
    element.style.textAlign = anchor.kind === "legend" ? "left" : "center";
    element.style.whiteSpace = "normal";
    element.style.willChange = "transform, left, top";
    if (anchor.kind === "legend") {
        const heading = document.createElement("div");
        heading.textContent = anchor.label;
        heading.style.fontSize = "10px";
        heading.style.fontWeight = "800";
        heading.style.lineHeight = "12px";
        heading.style.marginBottom = "5px";
        heading.style.opacity = "0.82";
        heading.style.textTransform = "uppercase";
        const body = document.createElement("div");
        body.textContent = anchor.value;
        body.style.fontSize = "12px";
        body.style.fontWeight = "800";
        body.style.lineHeight = "16px";
        element.append(heading, body);
        return element;
    }
    const eyebrow = document.createElement("div");
    eyebrow.textContent = anchor.label;
    eyebrow.style.fontSize = "10px";
    eyebrow.style.fontWeight = "800";
    eyebrow.style.lineHeight = "12px";
    eyebrow.style.opacity = "0.78";
    eyebrow.style.textTransform = "uppercase";
    const body = document.createElement("div");
    body.textContent = anchor.value;
    body.style.fontSize = "13px";
    body.style.fontWeight = "800";
    body.style.lineHeight = "16px";
    body.style.marginTop = "2px";
    element.append(eyebrow, body);
    return element;
};
const createDataVizChartLabelOverlay = ({ mountElement, renderer, camera, chartGroup, anchors, theme, }) => {
    const root = document.createElement("div");
    root.setAttribute("data-testid", "data-viz-three-chart-label-overlay");
    root.setAttribute("aria-label", "Three.js chart inline axis labels and legend");
    root.setAttribute("role", "group");
    root.style.position = "absolute";
    root.style.inset = "0";
    root.style.overflow = "hidden";
    root.style.pointerEvents = "none";
    root.style.zIndex = "2";
    const previousPosition = mountElement.style.position;
    const computedPosition = window.getComputedStyle(mountElement).position;
    if (computedPosition === "static") {
        mountElement.style.position = "relative";
    }
    const elements = new Map();
    anchors.forEach((anchor) => {
        const element = createDataVizChartOverlayLabelElement(anchor, theme);
        root.appendChild(element);
        elements.set(anchor.id, element);
    });
    mountElement.appendChild(root);
    const sync = () => {
        const width = renderer.domElement.clientWidth || renderer.domElement.width / Math.max(renderer.getPixelRatio(), 1);
        const height = renderer.domElement.clientHeight || renderer.domElement.height / Math.max(renderer.getPixelRatio(), 1);
        root.style.width = `${width}px`;
        root.style.height = `${height}px`;
        chartGroup.updateMatrixWorld(true);
        camera.updateMatrixWorld(true);
        anchors.forEach((anchor) => {
            const element = elements.get(anchor.id);
            if (!element) {
                return;
            }
            if (anchor.kind === "legend") {
                element.style.left = `${Math.max(width - 14, 0)}px`;
                element.style.top = "14px";
                element.style.transform = "translateX(-100%)";
                element.style.display = width >= 260 && height >= 180 ? "block" : "none";
                element.setAttribute("data-overlay-visible", element.style.display === "none" ? "false" : "true");
                return;
            }
            const projected = anchor.position.clone();
            chartGroup.localToWorld(projected);
            projected.project(camera);
            if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y) || !Number.isFinite(projected.z)) {
                element.style.display = "none";
                element.setAttribute("data-overlay-visible", "false");
                return;
            }
            const rawX = ((projected.x + 1) / 2) * width;
            const rawY = ((-projected.y + 1) / 2) * height;
            const elementWidth = element.offsetWidth || (anchor.kind === "y-axis" ? 150 : 180);
            const elementHeight = element.offsetHeight || 36;
            const safeX = clampDataVizOverlayPosition(rawX, elementWidth / 2 + 10, width - elementWidth / 2 - 10);
            const safeY = clampDataVizOverlayPosition(rawY, elementHeight / 2 + 10, height - elementHeight / 2 - 10);
            element.style.left = `${safeX}px`;
            element.style.top = `${safeY}px`;
            element.style.transform = "translate(-50%, -50%)";
            element.style.display = width >= 260 && height >= 180 ? "block" : "none";
            element.setAttribute("data-overlay-visible", element.style.display === "none" ? "false" : "true");
            element.setAttribute("data-overlay-projected-x", rawX.toFixed(1));
            element.setAttribute("data-overlay-projected-y", rawY.toFixed(1));
            element.setAttribute("data-overlay-clamped", String(Math.abs(rawX - safeX) > 0.5 || Math.abs(rawY - safeY) > 0.5));
        });
    };
    return {
        root,
        sync,
        dispose: () => {
            root.remove();
            if (computedPosition === "static") {
                mountElement.style.position = previousPosition;
            }
        },
    };
};
export const disposeDataVizThreeObject = (scene) => {
    scene.traverse((object) => {
        const mesh = object;
        mesh.geometry?.dispose();
        if (Array.isArray(mesh.material)) {
            mesh.material.forEach((material) => {
                material.map?.dispose();
                material.dispose();
            });
        }
        else {
            mesh.material?.map?.dispose();
            mesh.material?.dispose();
        }
    });
};
const resolveNodeColor = (node, theme) => dataVizColorToNumber(node.visual?.color ?? theme.nodeColors[node.kind] ?? theme.nodeFallbackColor);
const createNodeGeometry = (THREE, node) => {
    const size = node.visual?.size ?? (node.kind === "route" || node.kind === "report" ? 8 : 5.5);
    switch (node.visual?.shape) {
        case "box":
            return new THREE.BoxGeometry(size * 1.6, size * 1.6, size * 1.6);
        case "cylinder":
            return new THREE.CylinderGeometry(size * 0.7, size * 0.7, size * 1.5, 16);
        case "octahedron":
            return new THREE.OctahedronGeometry(size);
        case "tetrahedron":
            return new THREE.TetrahedronGeometry(size * 1.1);
        case "dodecahedron":
            return new THREE.DodecahedronGeometry(size);
        case "sphere":
        default:
            return new THREE.SphereGeometry(size, 24, 16);
    }
};
export const createDataVizThreeScene = (THREE, graphInput, options) => {
    const theme = resolveDataVizTheme(options.theme);
    const { graph, layout } = resolveDataVizLayout(graphInput, options.layout);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(theme.background);
    const camera = new THREE.PerspectiveCamera(theme.camera.fov, options.width / Math.max(options.height, 1), theme.camera.near, theme.camera.far);
    camera.position.set(...theme.camera.position);
    scene.add(new THREE.AmbientLight(0xffffff, theme.lighting.ambientIntensity));
    const directional = new THREE.DirectionalLight(0xffffff, theme.lighting.directionalIntensity);
    directional.position.set(...theme.lighting.directionalPosition);
    scene.add(directional);
    const graphGroup = new THREE.Group();
    scene.add(graphGroup);
    const nodePositions = new Map();
    graph.nodes.forEach((node) => {
        const positionTuple = layout.nodePositions[node.id] ?? node.position ?? [0, 0, 0];
        const position = new THREE.Vector3(...positionTuple);
        nodePositions.set(node.id, position);
        const color = resolveNodeColor(node, theme);
        const geometry = createNodeGeometry(THREE, node);
        const material = new THREE.MeshStandardMaterial({
            color,
            emissive: dataVizColorToNumber(node.visual?.emissiveColor ?? node.visual?.color ?? theme.nodeColors[node.kind] ?? theme.nodeFallbackColor),
            emissiveIntensity: node.visual?.emissiveIntensity ?? (node.status === "blocked" ? 0.16 : 0.06),
            roughness: node.visual?.roughness ?? 0.46,
            metalness: node.visual?.metalness ?? 0.12,
            transparent: node.visual?.opacity != null,
            opacity: node.visual?.opacity ?? 1,
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.copy(position);
        mesh.userData = {
            dataVizNodeId: node.id,
            dataVizNodeKind: node.kind,
            dataVizNodeStatus: node.status ?? "neutral",
        };
        graphGroup.add(mesh);
        const labelNodeKinds = options.labelNodeKinds ?? DEFAULT_LABEL_NODE_KINDS;
        if (labelNodeKinds.has(node.kind)) {
            const labelSprite = createDataVizLabelSprite(THREE, node.label, {
                ...theme,
                labelTextColor: node.visual?.labelColor ?? theme.labelTextColor,
                labelBackgroundColor: node.visual?.labelBackgroundColor ?? theme.labelBackgroundColor,
                labelBorderColor: node.visual?.labelBorderColor ?? theme.labelBorderColor,
            });
            if (labelSprite) {
                labelSprite.position.copy(position);
                labelSprite.position.y += node.kind === "route" || node.kind === "report" ? 16 : 12;
                labelSprite.position.z += 8;
                graphGroup.add(labelSprite);
            }
        }
    });
    for (const edge of graph.edges) {
        const source = nodePositions.get(edge.source);
        const target = nodePositions.get(edge.target);
        if (!source || !target) {
            continue;
        }
        const geometry = new THREE.BufferGeometry().setFromPoints([source, target]);
        const material = new THREE.LineBasicMaterial({
            color: dataVizColorToNumber(edge.visual?.color ?? theme.edgeColor),
            transparent: true,
            opacity: edge.visual?.opacity ?? theme.edgeOpacity,
        });
        graphGroup.add(new THREE.Line(geometry, material));
    }
    return { scene, camera, graphGroup, graph, layout, theme };
};
const DEFAULT_CHART_COLORS = [
    "#2697A2",
    "#237062",
    "#08BFAE",
    "#C57A00",
    "#7C3AED",
    "#CC4444",
    "#496A65",
    "#404F4D",
];
const formatDataVizChartValue = (value) => Math.abs(value) >= 1000
    ? value.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
const resolveDataVizChartPoints = (chart) => chart.series.flatMap((series, seriesIndex) => series.points
    .filter((point) => Number.isFinite(point.value))
    .map((point, pointIndex) => ({
    series,
    seriesIndex,
    point,
    pointIndex,
})));
const dataVizChartPointId = (series, point, index) => point.id ?? `${series.id}:${point.key || index}`;
const resolveDataVizChartColor = (point, series, index) => dataVizColorToNumber(point.visual?.color
    ?? series.color
    ?? DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length]);
const isDataVizBarChartKind = (kind) => kind === "bar" || kind === "grouped-bar";
const isDataVizLineChartKind = (kind) => kind === "line" || kind === "ribbon-line";
const addDataVizChartGrid = (THREE, group, theme, options) => {
    const gridColor = dataVizColorToNumber(theme.edgeColor);
    for (let tick = 0; tick <= 4; tick += 1) {
        const progress = tick / 4;
        const y = options.baseY + options.maxHeight * progress;
        const start = new THREE.Vector3(options.left, y, -22);
        const end = new THREE.Vector3(options.right, y, -22);
        const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
        const material = new THREE.LineBasicMaterial({
            color: gridColor,
            transparent: true,
            opacity: tick === 0 ? 0.44 : 0.2,
        });
        group.add(new THREE.Line(geometry, material));
        if (tick > 0) {
            const label = createDataVizLabelSprite(THREE, formatDataVizChartValue(options.maxValue * progress), theme);
            if (label) {
                label.position.set(options.left - 22, y, -18);
                label.scale.multiplyScalar(0.94);
                group.add(label);
            }
        }
    }
    const baselineGeometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(options.left, options.baseY, 18),
        new THREE.Vector3(options.right, options.baseY, 18),
    ]);
    const baselineMaterial = new THREE.LineBasicMaterial({
        color: gridColor,
        transparent: true,
        opacity: 0.56,
    });
    group.add(new THREE.Line(baselineGeometry, baselineMaterial));
};
const createDataVizChartSegment = (THREE, start, end, color, opacity, radius = 1.5) => {
    const direction = new THREE.Vector3().subVectors(end, start);
    const length = Math.max(direction.length(), 0.01);
    const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
    const geometry = new THREE.CylinderGeometry(radius, radius, length, 16);
    const material = new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.12,
        roughness: 0.38,
        metalness: 0.08,
        transparent: opacity < 1,
        opacity,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(midpoint);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
    return mesh;
};
export const createDataVizThreeChartScene = (THREE, chart, options) => {
    const theme = resolveDataVizTheme(options.theme);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(theme.background);
    const camera = new THREE.PerspectiveCamera(options.theme?.camera?.fov ?? 42, options.width / Math.max(options.height, 1), options.theme?.camera?.near ?? theme.camera.near, options.theme?.camera?.far ?? theme.camera.far);
    const cameraPosition = options.theme?.camera?.position ?? [0, 82, 268];
    camera.position.set(...cameraPosition);
    camera.lookAt(0, 4, 0);
    scene.add(new THREE.AmbientLight(0xffffff, theme.lighting.ambientIntensity));
    const directional = new THREE.DirectionalLight(0xffffff, theme.lighting.directionalIntensity);
    directional.position.set(...theme.lighting.directionalPosition);
    scene.add(directional);
    const rim = new THREE.DirectionalLight(0xffffff, 0.62);
    rim.position.set(-140, 80, -160);
    scene.add(rim);
    const chartGroup = new THREE.Group();
    chartGroup.rotation.x = -0.08;
    chartGroup.rotation.y = -0.16;
    scene.add(chartGroup);
    const resolvedPoints = resolveDataVizChartPoints(chart);
    const pointCount = resolvedPoints.length;
    const valueTotal = resolvedPoints.reduce((total, entry) => total + entry.point.value, 0);
    const maxPointValue = Math.max(...resolvedPoints.map((entry) => entry.point.value), 0);
    const explicitMax = chart.axes?.y?.max;
    const maxValue = Math.max(Number.isFinite(explicitMax) ? Number(explicitMax) : 0, maxPointValue, 1);
    const baseY = -70;
    const maxHeight = 140;
    const uniquePointCount = Math.max(...chart.series.map((series) => series.points.length), 1);
    const targetChartWidth = Math.min(460, Math.max(180, (uniquePointCount - 1) * 44));
    const xSpacing = uniquePointCount <= 1 ? 68 : targetChartWidth / Math.max(uniquePointCount - 1, 1);
    const left = -((uniquePointCount - 1) * xSpacing) / 2 - 42;
    const right = ((uniquePointCount - 1) * xSpacing) / 2 + 42;
    const seriesDepthSpacing = Math.min(chart.kind === "grouped-bar" ? 28 : 30, Math.max(chart.kind === "grouped-bar" ? 15 : 18, 96 / Math.max(chart.series.length, 1)));
    const overlayAnchors = [
        {
            id: "x-axis",
            kind: "x-axis",
            label: "X-axis",
            value: chart.axes?.x?.label?.trim() || "Bucket",
            position: new THREE.Vector3(0, baseY - 42, 34),
        },
        {
            id: "y-axis",
            kind: "y-axis",
            label: "Y-axis",
            value: chart.axes?.y?.label?.trim() || "Value",
            position: new THREE.Vector3(left - 54, baseY + maxHeight * 0.68, -4),
        },
        {
            id: "legend",
            kind: "legend",
            label: "Legend",
            value: chart.series.map((series) => series.label).join(" · ") || "Series",
            position: new THREE.Vector3(right + 28, baseY + maxHeight + 12, 0),
        },
    ];
    addDataVizChartGrid(THREE, chartGroup, theme, {
        left,
        right,
        baseY,
        maxHeight,
        maxValue,
    });
    const heightForValue = (value) => Math.max((Math.max(value, 0) / maxValue) * maxHeight, 1.2);
    if (isDataVizBarChartKind(chart.kind)) {
        chart.series.forEach((series, seriesIndex) => {
            const zOffset = (seriesIndex - (chart.series.length - 1) / 2) * seriesDepthSpacing;
            series.points.forEach((point, pointIndex) => {
                if (!Number.isFinite(point.value)) {
                    return;
                }
                const x = (pointIndex - (series.points.length - 1) / 2) * xSpacing;
                const height = heightForValue(point.value);
                const color = resolveDataVizChartColor(point, series, pointIndex + seriesIndex);
                const geometry = new THREE.BoxGeometry(Math.min(chart.kind === "grouped-bar" ? 26 : 34, xSpacing * 0.54), 1, chart.kind === "grouped-bar" ? 16 : 22);
                geometry.translate(0, 0.5, 0);
                const material = new THREE.MeshStandardMaterial({
                    color,
                    emissive: color,
                    emissiveIntensity: 0.08,
                    roughness: 0.34,
                    metalness: 0.16,
                    transparent: point.visual?.opacity != null,
                    opacity: point.visual?.opacity ?? 1,
                });
                const bar = new THREE.Mesh(geometry, material);
                bar.position.set(x, baseY, zOffset);
                bar.scale.y = options.reducedMotion ? height : 0.01;
                bar.userData = {
                    dataVizChartKind: chart.kind,
                    dataVizChartSeriesId: series.id,
                    dataVizChartPointId: dataVizChartPointId(series, point, pointIndex),
                    dataVizChartAnimation: "bar",
                    dataVizChartTargetScaleY: height,
                    dataVizChartRevealIndex: pointIndex,
                };
                chartGroup.add(bar);
                const valueLabel = createDataVizLabelSprite(THREE, formatDataVizChartValue(point.value), {
                    ...theme,
                    labelTextColor: point.visual?.labelColor ?? theme.labelTextColor,
                    labelBackgroundColor: point.visual?.labelBackgroundColor ?? theme.labelBackgroundColor,
                    labelBorderColor: point.visual?.labelBorderColor ?? theme.labelBorderColor,
                });
                if (valueLabel && (chart.series.length <= 2 || pointIndex % 2 === 0)) {
                    valueLabel.position.set(x, baseY + height + 13, zOffset + 2);
                    valueLabel.scale.multiplyScalar(1.12);
                    chartGroup.add(valueLabel);
                }
                const xLabel = createDataVizLabelSprite(THREE, point.label, theme);
                if (xLabel && seriesIndex === 0) {
                    xLabel.position.set(x, baseY - 20, 18);
                    xLabel.scale.multiplyScalar(1.02);
                    chartGroup.add(xLabel);
                }
            });
            const seriesLabel = createDataVizLabelSprite(THREE, series.label, {
                ...theme,
                labelTextColor: theme.labelTextColor,
                labelBackgroundColor: theme.labelBackgroundColor,
                labelBorderColor: series.color ?? theme.labelBorderColor,
            });
            if (seriesLabel) {
                seriesLabel.position.set(right + 18, baseY + maxHeight - (seriesIndex * 22), zOffset);
                seriesLabel.scale.multiplyScalar(0.96);
                chartGroup.add(seriesLabel);
            }
        });
    }
    if (isDataVizLineChartKind(chart.kind)) {
        chart.series.forEach((series, seriesIndex) => {
            const zOffset = (seriesIndex - (chart.series.length - 1) / 2) * seriesDepthSpacing;
            const positions = [];
            series.points.forEach((point, pointIndex) => {
                if (!Number.isFinite(point.value)) {
                    return;
                }
                const x = (pointIndex - (series.points.length - 1) / 2) * xSpacing;
                const y = baseY + heightForValue(point.value);
                const position = new THREE.Vector3(x, y, zOffset);
                positions.push(position);
                const color = resolveDataVizChartColor(point, series, pointIndex + seriesIndex);
                const geometry = new THREE.SphereGeometry(5.5, 24, 16);
                const material = new THREE.MeshStandardMaterial({
                    color,
                    emissive: color,
                    emissiveIntensity: 0.14,
                    roughness: 0.28,
                    metalness: 0.12,
                    transparent: point.visual?.opacity != null,
                    opacity: point.visual?.opacity ?? 1,
                });
                const pointMesh = new THREE.Mesh(geometry, material);
                pointMesh.position.copy(position);
                if (!options.reducedMotion) {
                    pointMesh.scale.setScalar(0.01);
                }
                pointMesh.userData = {
                    dataVizChartKind: chart.kind,
                    dataVizChartSeriesId: series.id,
                    dataVizChartPointId: dataVizChartPointId(series, point, pointIndex),
                    dataVizChartAnimation: "point",
                    dataVizChartRevealIndex: pointIndex,
                };
                chartGroup.add(pointMesh);
                if (pointIndex > 0) {
                    const previous = positions[positions.length - 2];
                    const segment = createDataVizChartSegment(THREE, previous, position, color, point.visual?.opacity ?? 1, chart.kind === "ribbon-line" ? 2.4 : 1.5);
                    if (!options.reducedMotion) {
                        segment.scale.y = 0.01;
                    }
                    segment.userData = {
                        dataVizChartKind: chart.kind,
                        dataVizChartSeriesId: series.id,
                        dataVizChartPointId: dataVizChartPointId(series, point, pointIndex),
                        dataVizChartAnimation: "segment",
                        dataVizChartRevealIndex: pointIndex,
                    };
                    chartGroup.add(segment);
                }
                const valueLabel = createDataVizLabelSprite(THREE, formatDataVizChartValue(point.value), {
                    ...theme,
                    labelTextColor: point.visual?.labelColor ?? theme.labelTextColor,
                    labelBackgroundColor: point.visual?.labelBackgroundColor ?? theme.labelBackgroundColor,
                    labelBorderColor: point.visual?.labelBorderColor ?? theme.labelBorderColor,
                });
                if (valueLabel && (chart.series.length <= 2 || pointIndex % 2 === 0)) {
                    valueLabel.position.set(x, y + 14, zOffset + 2);
                    valueLabel.scale.multiplyScalar(1.08);
                    chartGroup.add(valueLabel);
                }
                const xLabel = createDataVizLabelSprite(THREE, point.label, theme);
                if (xLabel && seriesIndex === 0) {
                    xLabel.position.set(x, baseY - 20, 18);
                    xLabel.scale.multiplyScalar(0.98);
                    chartGroup.add(xLabel);
                }
            });
            const seriesLabel = createDataVizLabelSprite(THREE, series.label, {
                ...theme,
                labelTextColor: theme.labelTextColor,
                labelBackgroundColor: theme.labelBackgroundColor,
                labelBorderColor: series.color ?? theme.labelBorderColor,
            });
            if (seriesLabel) {
                seriesLabel.position.set(right + 18, baseY + maxHeight - (seriesIndex * 22), zOffset);
                seriesLabel.scale.multiplyScalar(0.96);
                chartGroup.add(seriesLabel);
            }
        });
    }
    if (chart.title) {
        const title = createDataVizLabelSprite(THREE, chart.title, theme);
        if (title) {
            title.position.set(0, baseY + maxHeight + 34, 0);
            title.scale.multiplyScalar(1.06);
            chartGroup.add(title);
        }
    }
    return {
        scene,
        camera,
        chartGroup,
        chart,
        theme,
        pointCount,
        valueTotal,
        overlayAnchors,
    };
};
const tupleFromVector = (vector) => [
    vector.x,
    vector.y,
    vector.z,
];
export const mountDataVizThreeScene = (options) => {
    const { THREE, OrbitControls, mountElement, graph, testId = "data-viz-three-canvas", ariaLabel = "Data visualization graph", minWidth = 360, minHeight = 280, dprCap = 2, preserveDrawingBuffer = true, } = options;
    const width = Math.max(mountElement.clientWidth || 720, minWidth);
    const height = Math.max(mountElement.clientHeight || 320, minHeight);
    const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        preserveDrawingBuffer,
    });
    const pixelRatio = Math.min(window.devicePixelRatio || 1, dprCap);
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width, height);
    renderer.domElement.setAttribute("data-testid", testId);
    renderer.domElement.setAttribute("aria-label", ariaLabel);
    mountElement.replaceChildren(renderer.domElement);
    const sceneState = createDataVizThreeScene(THREE, graph, {
        width,
        height,
        theme: options.theme,
        layout: options.layout,
    });
    const { scene, camera, graphGroup } = sceneState;
    const controls = OrbitControls ? new OrbitControls(camera, renderer.domElement) : null;
    if (controls) {
        controls.enablePan = options.controls?.enablePan ?? true;
        controls.enableZoom = options.controls?.enableZoom ?? true;
        controls.enableRotate = options.controls?.enableRotate ?? false;
        controls.screenSpacePanning = options.controls?.screenSpacePanning ?? true;
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.mouseButtons.LEFT = THREE.MOUSE.PAN;
        controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
        controls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
    }
    const defaultCameraPosition = camera.position.clone();
    const defaultCameraTarget = controls?.target.clone() ?? new THREE.Vector3(0, 0, 0);
    let frame = 0;
    let disposed = false;
    let isUpdatingControls = false;
    let currentDiagnostics = {
        cameraPosition: serializeDataVizVector(camera.position),
        cameraTarget: serializeDataVizVector(controls?.target ?? defaultCameraTarget),
        viewport: {
            width,
            height,
            pixelRatio,
        },
    };
    const syncCameraDiagnostics = () => {
        currentDiagnostics = {
            cameraPosition: serializeDataVizVector(camera.position),
            cameraTarget: serializeDataVizVector(controls?.target ?? defaultCameraTarget),
            viewport: {
                width: renderer.domElement.width,
                height: renderer.domElement.height,
                pixelRatio: renderer.getPixelRatio(),
            },
        };
        options.onDiagnosticsChange?.(currentDiagnostics);
    };
    // OrbitControls may synchronously emit "change" from update(); draw handlers must not update controls.
    const drawScene = () => {
        renderer.render(scene, camera);
    };
    const updateControls = () => {
        if (!controls) {
            return;
        }
        isUpdatingControls = true;
        try {
            controls.update();
        }
        finally {
            isUpdatingControls = false;
        }
    };
    const render = () => {
        updateControls();
        drawScene();
    };
    const resetView = () => {
        graphGroup.rotation.set(0, 0, 0);
        camera.position.copy(defaultCameraPosition);
        controls?.target.copy(defaultCameraTarget);
        updateControls();
        syncCameraDiagnostics();
        drawScene();
    };
    const shouldAutoRotate = () => {
        if (options.reducedMotion) {
            return false;
        }
        return typeof options.autoRotate === "function"
            ? options.autoRotate()
            : Boolean(options.autoRotate);
    };
    const animate = () => {
        if (disposed) {
            return;
        }
        if (shouldAutoRotate()) {
            graphGroup.rotation.y += 0.0035;
            graphGroup.rotation.x = Math.sin(Date.now() / 1800) * 0.08;
        }
        render();
        frame = window.requestAnimationFrame(animate);
    };
    const pauseAutoRotate = () => {
        options.onPauseAutoRotate?.();
    };
    const handleControlsChange = () => {
        if (isUpdatingControls) {
            syncCameraDiagnostics();
            return;
        }
        syncCameraDiagnostics();
        drawScene();
    };
    const handleResize = () => {
        const nextWidth = Math.max(mountElement.clientWidth || width, minWidth);
        const nextHeight = Math.max(mountElement.clientHeight || height, minHeight);
        renderer.setSize(nextWidth, nextHeight);
        camera.aspect = nextWidth / Math.max(nextHeight, 1);
        camera.updateProjectionMatrix();
        updateControls();
        syncCameraDiagnostics();
        drawScene();
    };
    const resizeObserver = typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(handleResize)
        : null;
    controls?.addEventListener("start", pauseAutoRotate);
    controls?.addEventListener("change", handleControlsChange);
    renderer.domElement.addEventListener("wheel", pauseAutoRotate, { passive: true });
    window.addEventListener("resize", handleResize);
    resizeObserver?.observe(mountElement);
    syncCameraDiagnostics();
    handleResize();
    animate();
    return {
        resetView,
        render,
        getDiagnostics: () => ({
            ...currentDiagnostics,
            viewport: { ...currentDiagnostics.viewport },
        }),
        dispose: () => {
            disposed = true;
            graphGroup.rotation.set(...tupleFromVector(graphGroup.rotation));
            resizeObserver?.disconnect();
            window.removeEventListener("resize", handleResize);
            renderer.domElement.removeEventListener("wheel", pauseAutoRotate);
            controls?.removeEventListener("start", pauseAutoRotate);
            controls?.removeEventListener("change", handleControlsChange);
            controls?.dispose();
            if (frame) {
                window.cancelAnimationFrame(frame);
            }
            disposeDataVizThreeObject(scene);
            renderer.dispose();
            if (mountElement.contains(renderer.domElement)) {
                mountElement.removeChild(renderer.domElement);
            }
        },
    };
};
export const mountDataVizThreeChartScene = (options) => {
    const { THREE, OrbitControls, mountElement, chart, testId = "data-viz-three-chart-canvas", ariaLabel = "Data visualization chart", minWidth = 360, minHeight = 280, dprCap = 2, preserveDrawingBuffer = true, } = options;
    const width = Math.max(mountElement.clientWidth || 720, minWidth);
    const height = Math.max(mountElement.clientHeight || 320, minHeight);
    const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        preserveDrawingBuffer,
    });
    const pixelRatio = Math.min(window.devicePixelRatio || 1, dprCap);
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width, height);
    renderer.domElement.setAttribute("data-testid", testId);
    renderer.domElement.setAttribute("aria-label", ariaLabel);
    renderer.domElement.setAttribute("data-chart-kind", chart.kind);
    renderer.domElement.setAttribute("data-chart-render-kind", chart.kind);
    renderer.domElement.setAttribute("data-chart-series-count", String(chart.series.length));
    renderer.domElement.setAttribute("data-chart-point-count", String(chart.series.reduce((total, series) => total + series.points.length, 0)));
    mountElement.replaceChildren(renderer.domElement);
    const sceneState = createDataVizThreeChartScene(THREE, chart, {
        width,
        height,
        theme: options.theme,
        reducedMotion: options.reducedMotion,
    });
    const { scene, camera, chartGroup, valueTotal, overlayAnchors, theme } = sceneState;
    renderer.domElement.setAttribute("data-chart-value-total", String(valueTotal));
    renderer.domElement.setAttribute("data-chart-label-overlay", "screen-space-dom");
    const controls = OrbitControls ? new OrbitControls(camera, renderer.domElement) : null;
    if (controls) {
        controls.enablePan = options.controls?.enablePan ?? true;
        controls.enableZoom = options.controls?.enableZoom ?? true;
        controls.enableRotate = options.controls?.enableRotate ?? true;
        controls.screenSpacePanning = options.controls?.screenSpacePanning ?? true;
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
        controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
        controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
        controls.target.set(0, 0, 0);
    }
    const defaultCameraPosition = camera.position.clone();
    const defaultCameraTarget = controls?.target.clone() ?? new THREE.Vector3(0, 0, 0);
    const animationStartedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    let frame = 0;
    let disposed = false;
    let isUpdatingControls = false;
    const labelOverlay = createDataVizChartLabelOverlay({
        mountElement,
        renderer,
        camera,
        chartGroup,
        anchors: overlayAnchors,
        theme,
    });
    let currentDiagnostics = {
        cameraPosition: serializeDataVizVector(camera.position),
        cameraTarget: serializeDataVizVector(controls?.target ?? defaultCameraTarget),
        viewport: {
            width,
            height,
            pixelRatio,
        },
    };
    const syncCameraDiagnostics = () => {
        currentDiagnostics = {
            cameraPosition: serializeDataVizVector(camera.position),
            cameraTarget: serializeDataVizVector(controls?.target ?? defaultCameraTarget),
            viewport: {
                width: renderer.domElement.width,
                height: renderer.domElement.height,
                pixelRatio: renderer.getPixelRatio(),
            },
        };
        options.onDiagnosticsChange?.(currentDiagnostics);
    };
    const updateChartAnimation = () => {
        if (options.reducedMotion) {
            return;
        }
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        const elapsed = Math.min((now - animationStartedAt) / 1100, 1);
        chartGroup.traverse((object) => {
            const animation = object.userData?.dataVizChartAnimation;
            if (!animation) {
                return;
            }
            const revealIndex = Number(object.userData.dataVizChartRevealIndex ?? 0);
            const delay = Math.min(revealIndex * 0.08, 0.42);
            const progress = Math.max(0, Math.min((elapsed - delay) / Math.max(1 - delay, 0.01), 1));
            const eased = 1 - ((1 - progress) ** 3);
            if (animation === "bar") {
                const targetScaleY = Number(object.userData.dataVizChartTargetScaleY ?? 1);
                object.scale.y = Math.max(targetScaleY * eased, 0.01);
                return;
            }
            if (animation === "point") {
                object.scale.setScalar(Math.max(eased, 0.01));
                return;
            }
            object.scale.y = Math.max(eased, 0.01);
        });
    };
    // OrbitControls may synchronously emit "change" from update(); draw handlers must not update controls.
    const drawScene = () => {
        renderer.render(scene, camera);
        labelOverlay.sync();
    };
    const updateControls = () => {
        if (!controls) {
            return;
        }
        isUpdatingControls = true;
        try {
            controls.update();
        }
        finally {
            isUpdatingControls = false;
        }
    };
    const render = () => {
        updateChartAnimation();
        updateControls();
        drawScene();
    };
    const resetView = () => {
        chartGroup.rotation.set(-0.08, -0.16, 0);
        camera.position.copy(defaultCameraPosition);
        controls?.target.copy(defaultCameraTarget);
        updateControls();
        syncCameraDiagnostics();
        updateChartAnimation();
        drawScene();
    };
    const shouldAutoRotate = () => {
        if (options.reducedMotion) {
            return false;
        }
        return typeof options.autoRotate === "function"
            ? options.autoRotate()
            : Boolean(options.autoRotate);
    };
    const animate = () => {
        if (disposed) {
            return;
        }
        if (shouldAutoRotate()) {
            chartGroup.rotation.y += 0.0026;
        }
        render();
        frame = window.requestAnimationFrame(animate);
    };
    const pauseAutoRotate = () => {
        options.onPauseAutoRotate?.();
    };
    const handleControlsChange = () => {
        if (isUpdatingControls) {
            syncCameraDiagnostics();
            return;
        }
        syncCameraDiagnostics();
        updateChartAnimation();
        drawScene();
    };
    const handleResize = () => {
        const nextWidth = Math.max(mountElement.clientWidth || width, minWidth);
        const nextHeight = Math.max(mountElement.clientHeight || height, minHeight);
        renderer.setSize(nextWidth, nextHeight);
        camera.aspect = nextWidth / Math.max(nextHeight, 1);
        camera.updateProjectionMatrix();
        updateControls();
        syncCameraDiagnostics();
        updateChartAnimation();
        drawScene();
    };
    const resizeObserver = typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(handleResize)
        : null;
    controls?.addEventListener("start", pauseAutoRotate);
    controls?.addEventListener("change", handleControlsChange);
    renderer.domElement.addEventListener("wheel", pauseAutoRotate, { passive: true });
    window.addEventListener("resize", handleResize);
    resizeObserver?.observe(mountElement);
    syncCameraDiagnostics();
    handleResize();
    animate();
    return {
        resetView,
        render,
        getDiagnostics: () => ({
            ...currentDiagnostics,
            viewport: { ...currentDiagnostics.viewport },
        }),
        dispose: () => {
            disposed = true;
            resizeObserver?.disconnect();
            window.removeEventListener("resize", handleResize);
            renderer.domElement.removeEventListener("wheel", pauseAutoRotate);
            controls?.removeEventListener("start", pauseAutoRotate);
            controls?.removeEventListener("change", handleControlsChange);
            controls?.dispose();
            labelOverlay.dispose();
            if (frame) {
                window.cancelAnimationFrame(frame);
            }
            disposeDataVizThreeObject(scene);
            renderer.dispose();
            if (mountElement.contains(renderer.domElement)) {
                mountElement.removeChild(renderer.domElement);
            }
        },
    };
};
//# sourceMappingURL=index.js.map